import { QueryStringValues, TCurry2 } from '@x/types';
import {
  setAndPushQueryString,
  setAndReplaceQueryString,
  updateQueryStringWithDefaults,
} from '@x/utils';
import * as R from 'ramda';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

type TUseUpdateQueryString = {
  pushQueryString: (newQsValues: QueryStringValues) => void;
  pushQueryStringWithDefaults: TCurry2<
    QueryStringValues,
    QueryStringValues,
    void
  >;
  replaceQueryString: (newQsValues: QueryStringValues) => void;
  replaceQueryStringWithDefaults: TCurry2<
    QueryStringValues,
    QueryStringValues,
    void
  >;
};

export function useUpdateQueryString(): TUseUpdateQueryString {
  const history = useHistory();

  const pushQueryString = useCallback(
    (newQsValues: QueryStringValues) =>
      R.pipe(
        updateQueryStringWithDefaults(history, {}),
        setAndPushQueryString(history),
      )(newQsValues),
    [history],
  );

  const pushQueryStringWithDefaults = useMemo(
    () =>
      R.curry(
        (defaultQsValues: QueryStringValues, newQsValues: QueryStringValues) =>
          R.pipe(
            updateQueryStringWithDefaults(history, defaultQsValues),
            setAndPushQueryString(history),
          )(newQsValues),
      ),
    [history],
  );

  const replaceQueryString = useCallback(
    (newQsValues: QueryStringValues) =>
      R.pipe(
        updateQueryStringWithDefaults(history, {}),
        setAndReplaceQueryString(history),
      )(newQsValues),
    [history],
  );

  const replaceQueryStringWithDefaults = useMemo(
    () =>
      R.curry(
        (defaultQsValues: QueryStringValues, newQsValues: QueryStringValues) =>
          R.pipe(
            updateQueryStringWithDefaults(history, defaultQsValues),
            setAndReplaceQueryString(history),
          )(newQsValues),
      ),
    [history],
  );

  return {
    pushQueryString,
    pushQueryStringWithDefaults,
    replaceQueryString,
    replaceQueryStringWithDefaults,
  };
}
